import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

// Admin "Se connecter en tant que": the admin's own JWT (if any) is parked here
// while the short-lived impersonation JWT lives in `gantt_token`. Google admins
// have no JWT — their cookie session is untouched and resumes on exit.
const ADMIN_TOKEN_BACKUP = "gantt_admin_token_backup";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      // Unauthenticated or expired session — API interceptor already handled 401.
      if (e?.response?.status !== 401) {
        console.warn("checkAuth failed:", e?.message || e);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const loginWithToken = (token, userData) => {
    localStorage.setItem("gantt_token", token);
    setUser(userData);
  };

  const startImpersonation = (token, userData) => {
    const current = localStorage.getItem("gantt_token");
    // Never overwrite an existing backup (would lose the admin's real token).
    if (!localStorage.getItem(ADMIN_TOKEN_BACKUP)) {
      localStorage.setItem(ADMIN_TOKEN_BACKUP, current || "");
    }
    localStorage.setItem("gantt_token", token);
    setUser(userData);
  };

  const stopImpersonation = async () => {
    const backup = localStorage.getItem(ADMIN_TOKEN_BACKUP);
    if (backup) localStorage.setItem("gantt_token", backup);
    else localStorage.removeItem("gantt_token");
    localStorage.removeItem(ADMIN_TOKEN_BACKUP);
    await checkAuth();
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // Server-side session may already be gone — non-fatal.
      console.warn("logout API call failed:", e?.message || e);
    }
    localStorage.removeItem("gantt_token");
    localStorage.removeItem(ADMIN_TOKEN_BACKUP);
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user, setUser, loading, checkAuth, loginWithToken, logout, refreshUser: checkAuth,
        startImpersonation, stopImpersonation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
